/**
 * Data-age stamp for the competitor-watch surfaces.
 *
 * The competitor-watch API returns `generated_at`, but that is only
 * `datetime.now()` at request time — it says nothing about how old the numbers
 * are. The numbers themselves come from `brand_metrics`, a table written
 * exclusively by a manual Social Insider CSV import that no scheduled job runs.
 * So the data ages silently, and the page kept looking healthy straight through
 * an eight-day Instagram ingest outage because it never read that data at all.
 *
 * This renders the two timestamps that are actually true: the last day the
 * metrics describe, and when we loaded them. Past `staleAfterDays` it turns
 * amber, so "nobody has re-run the import since May" is visible on the page
 * rather than something you have to go read the database to discover.
 */

import { Box, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { colors } from '../theme';

/** Days after which the snapshot is called out as stale. */
const DEFAULT_STALE_AFTER_DAYS = 45;

export interface DataFreshnessProps {
  dataThrough: string | null | undefined;
  dataIngestedAt?: string | null;
  dataSource?: string | null;
  staleAfterDays?: number;
  /** `compact` drops the ingest/source detail — for the summary tile. */
  variant?: 'full' | 'compact';
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function daysSince(iso: string): number {
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
}

/** "3 months" / "12 days" — coarse on purpose; precision isn't the point. */
function humanAge(days: number): string {
  if (days < 1) return 'today';
  if (days === 1) return '1 day';
  if (days < 45) return `${days} days`;
  const months = Math.round(days / 30);
  if (months < 18) return `${months} month${months === 1 ? '' : 's'}`;
  return `${Math.round(days / 365)} year${Math.round(days / 365) === 1 ? '' : 's'}`;
}

export function DataFreshness({
  dataThrough,
  dataIngestedAt,
  dataSource,
  staleAfterDays = DEFAULT_STALE_AFTER_DAYS,
  variant = 'full',
}: DataFreshnessProps) {
  // No timestamp is itself worth saying. Rendering nothing would leave the
  // silent-staleness gap exactly as it was.
  if (!dataThrough) {
    return (
      <Typography sx={{ fontSize: 12, color: colors.textMuted }}>
        Snapshot date unavailable
      </Typography>
    );
  }

  const age = daysSince(dataThrough);
  const isStale = age > staleAfterDays;
  const tone = isStale ? colors.warning : colors.textMuted;

  const detail = [
    `Metrics cover the period ending ${fmtDate(dataThrough)} (${humanAge(age)} old).`,
    dataIngestedAt ? `Loaded into the database on ${fmtDate(dataIngestedAt)}.` : null,
    dataSource ? `Source: ${dataSource.replace(/_/g, ' ')}.` : null,
    'This is a periodic snapshot import, not the nightly Instagram ingest — it only refreshes when the export is re-run.',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Tooltip title={detail} arrow>
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          color: tone,
          cursor: 'help',
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 14 }} />
        <Typography sx={{ fontSize: 12, fontWeight: isStale ? 700 : 500, color: 'inherit' }}>
          {variant === 'compact'
            ? `Data through ${fmtDate(dataThrough)}`
            : `Data through ${fmtDate(dataThrough)} · ${humanAge(age)} old`}
          {isStale && variant === 'full' ? ' · stale' : ''}
        </Typography>
      </Box>
    </Tooltip>
  );
}
